import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Stack,
  Typography,
  Tabs,
  Tab,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import SaveIcon from "@mui/icons-material/Save";
import DownloadIcon from "@mui/icons-material/Download";
import DashboardLayout from "../../../layouts/Dashboard";
import FilterDatePicker from "../../../components/DatePicker/FilterDatePiecker";
import useRoles from "../../../hooks/useRoles";
import {
  PROVISION_LABORATORY_LABELS,
  resolveTitle,
  toApiDate,
  parseDecimalComma,
  formatReportDateRu,
  getProvisionAccess,
  resolveLaboratoryTypeIdFromEmployee,
  canEditBranchRow,
} from "../../../models/provisionReport";
import {
  provisionReportApi,
  ensureTabInData,
  resolveActiveColumns,
  mergeBranchRows,
} from "../../../http/provisionReportApi";
import ProvisionReportGrid from "./ProvisionReportGrid";
import { exportProvisionReportExcel } from "./provisionReportExcel";
import { departmentCRUD } from "../../../http";

const AUTOSAVE_MS = 700;

function startOfToday() {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function isReportDateToday(date) {
  if (!date) return false;
  const selected = toApiDate(date);
  const today = toApiDate(startOfToday());
  return selected != null && selected === today;
}

const ProvisionReport = () => {
  const { laboratoryTypeId: labParam } = useParams();
  const laboratoryTypeId = Number(labParam) || 1;
  const { employee, roles } = useRoles();
  const [employeeWithDepartment, setEmployeeWithDepartment] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const hydrateDepartment = async () => {
      if (!employee) {
        if (!cancelled) setEmployeeWithDepartment(null);
        return;
      }
      if (
        employee.department?.name ||
        resolveLaboratoryTypeIdFromEmployee(employee) != null
      ) {
        if (!cancelled) setEmployeeWithDepartment(employee);
        return;
      }
      if (!employee.departmentId) {
        if (!cancelled) setEmployeeWithDepartment(employee);
        return;
      }
      try {
        const dept = await departmentCRUD.get(employee.departmentId);
        if (!cancelled) {
          setEmployeeWithDepartment({
            ...employee,
            department: dept,
          });
        }
      } catch (e) {
        if (!cancelled) setEmployeeWithDepartment(employee);
      }
    };
    hydrateDepartment();
    return () => {
      cancelled = true;
    };
  }, [employee?.id, employee?.departmentId, employee?.department?.name]);

  const access = useMemo(
    () => getProvisionAccess(roles, employeeWithDepartment),
    [roles, employeeWithDepartment]
  );
  const canAccessThisLab = access.canAccessLaboratory(laboratoryTypeId);

  const [reportDate, setReportDate] = useState(startOfToday);
  const isToday = isReportDateToday(reportDate);
  // редактирование только за сегодня + права роли
  const isReadonly = access.isReadonly || !isToday;
  const [schema, setSchema] = useState(null);
  const [report, setReport] = useState(null);
  const [activeSectionKey, setActiveSectionKey] = useState(null);
  const [fundingMode, setFundingMode] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(""); // '', 'saving', 'saved', 'error'
  const [saveError, setSaveError] = useState("");
  const [schemaFallback, setSchemaFallback] = useState(false);
  const [exporting, setExporting] = useState(false);

  const dirtyRef = useRef(false);
  const reportRef = useRef(null);
  const saveTimerRef = useRef(null);

  useEffect(() => {
    reportRef.current = report;
  }, [report]);

  const labTitle =
    schema?.laboratoryName ||
    PROVISION_LABORATORY_LABELS[laboratoryTypeId] ||
    "Обеспеченность";

  const activeTabMeta = useMemo(() => {
    if (!schema?.tabs?.length) return null;
    return (
      schema.tabs.find((t) => t.sectionKey === activeSectionKey) ||
      schema.tabs[0]
    );
  }, [schema, activeSectionKey]);

  const branchList = useMemo(
    () => activeTabMeta?.branches || schema?.branches || null,
    [activeTabMeta, schema]
  );

  const activeColumns = useMemo(
    () => resolveActiveColumns(activeTabMeta, fundingMode),
    [activeTabMeta, fundingMode]
  );

  const pageTitle = useMemo(() => {
    if (!activeTabMeta) return `Обеспеченность — ${labTitle}`;
    const financeLabel = activeTabMeta.fundingModes?.find(
      (m) => m.key === fundingMode
    )?.title;
    return resolveTitle(activeTabMeta.titleTemplate, reportDate, {
      finance: financeLabel,
    });
  }, [activeTabMeta, reportDate, labTitle, fundingMode]);

  const gridRows = useMemo(() => {
    if (!report?.data || !activeTabMeta?.sectionKey) return [];
    const { tab } = ensureTabInData(
      report.data,
      activeTabMeta.sectionKey,
      branchList
    );
    return tab.rows || [];
  }, [report, activeTabMeta, branchList]);

  const loadAll = useCallback(
    async (dateValue) => {
      setLoading(true);
      setSaveStatus("");
      setSaveError("");
      try {
        const { schema: nextSchema, fromFallback } =
          await provisionReportApi.getSchema(laboratoryTypeId);

        setSchemaFallback(fromFallback);
        setSchema(nextSchema);

        const firstTab = nextSchema?.tabs?.[0];
        setActiveSectionKey((prev) => {
          if (prev && nextSchema?.tabs?.some((t) => t.sectionKey === prev)) {
            return prev;
          }
          return firstTab?.sectionKey || null;
        });
        if (firstTab?.fundingModes?.length) {
          setFundingMode((prev) => {
            if (prev && firstTab.fundingModes.some((m) => m.key === prev)) {
              return prev;
            }
            return firstTab.fundingModes[0].key;
          });
        } else {
          setFundingMode(null);
        }

        let nextReport;
        try {
          nextReport = await provisionReportApi.getCurrent(
            laboratoryTypeId,
            dateValue
          );
        } catch (e) {
          setSaveError(
            typeof e === "string"
              ? e
              : e?.message || "Ошибка загрузки отчёта (current)"
          );
          nextReport = {
            id: null,
            laboratoryTypeId,
            reportDate: toApiDate(dateValue),
            data: { tabs: [] },
          };
        }

        let data = nextReport.data || { tabs: [] };
        (nextSchema?.tabs || []).forEach((t) => {
          const branches = t.branches || nextSchema.branches || null;
          const ensured = ensureTabInData(data, t.sectionKey, branches);
          data = {
            tabs: ensured.data.tabs.map((tab) =>
              tab.sectionKey === t.sectionKey
                ? { ...tab, rows: mergeBranchRows(tab.rows, branches) }
                : tab
            ),
          };
        });

        setReport({
          ...nextReport,
          laboratoryTypeId,
          reportDate: toApiDate(dateValue) || nextReport.reportDate,
          data,
        });
        dirtyRef.current = false;
      } catch (e) {
        setSaveError(typeof e === "string" ? e : e?.message || "Ошибка загрузки");
      } finally {
        setLoading(false);
      }
    },
    [laboratoryTypeId]
  );

  useEffect(() => {
    if (!access.canView || !canAccessThisLab) return;
    loadAll(reportDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laboratoryTypeId, canAccessThisLab, access.canView]);

  const persist = useCallback(async (nextReport) => {
    if (!nextReport) return;
    if (!isReportDateToday(nextReport.reportDate || reportDate)) {
      setSaveStatus("error");
      setSaveError("Редактирование доступно только за текущую дату.");
      return;
    }
    setSaving(true);
    setSaveStatus("saving");
    setSaveError("");
    try {
      // нормализуем decimal-строки в number перед отправкой
      const normalized = normalizeDataForSave(nextReport);
      const isProvisionSupervisorPut =
        normalized.id != null &&
        normalized.id !== "" &&
        roles.includes("ProvisionSupervisor");
      const payload = isProvisionSupervisorPut
        ? {
            ...normalized,
            data: {
              ...normalized.data,
              tabs: (normalized.data?.tabs || []).map((tab) => ({
                ...tab,
                rows: (tab.rows || []).filter((row) =>
                  canEditBranchRow(row, employeeWithDepartment, {
                    ...access,
                    canEdit: true,
                    isFullEditor: false,
                  })
                ),
              })),
            },
          }
        : normalized;
      const saved = await provisionReportApi.save(payload);
      dirtyRef.current = false;
      if (saved?.id) {
        setReport((prev) => ({
          ...normalized,
          id: saved.id,
        }));
      } else {
        setReport(normalized);
      }
      setSaveStatus("saved");
    } catch (e) {
      setSaveStatus("error");
      setSaveError(typeof e === "string" ? e : e?.message || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  }, [reportDate, roles, employeeWithDepartment, access]);

  const scheduleAutosave = useCallback(
    (nextReport) => {
      dirtyRef.current = true;
      if (schemaFallback || isReadonly) {
        setSaveStatus("");
        return;
      }
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(() => {
        persist(nextReport);
      }, AUTOSAVE_MS);
    },
    [persist, schemaFallback, isReadonly]
  );

  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const handleDateChange = async (value) => {
    if (!value) return;
    const d = value instanceof Date ? value : new Date(value);
    d.setHours(0, 0, 0, 0);
    setReportDate(d);
    if (dirtyRef.current && reportRef.current) {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      await persist(reportRef.current);
    }
    await loadAll(d);
  };

  const handleSectionChange = (_e, value) => {
    setActiveSectionKey(value);
    const meta = schema?.tabs?.find((t) => t.sectionKey === value);
    if (meta?.fundingModes?.length) {
      setFundingMode(meta.fundingModes[0].key);
    } else {
      setFundingMode(null);
    }
  };

  const handleCellChange = (branchKey, cellKey, value, col) => {
    if (isReadonly) return;
    setReport((prev) => {
      if (!prev || !activeTabMeta) return prev;
      const ensured = ensureTabInData(
        prev.data,
        activeTabMeta.sectionKey,
        branchList
      );
      let changed = false;
      const tabs = ensured.data.tabs.map((t) => {
        if (t.sectionKey !== ensured.tab.sectionKey) return t;
        return {
          ...t,
          rows: t.rows.map((r) => {
            if (r.branchKey !== branchKey) return r;
            if (!canEditBranchRow(r, employeeWithDepartment, access)) {
              return r;
            }
            changed = true;
            let storeValue = value;
            if (col?.type === "number" && value !== null && value !== "") {
              storeValue = Number(value);
            }
            return {
              ...r,
              cells: {
                ...r.cells,
                [cellKey]: storeValue,
              },
            };
          }),
        };
      });
      if (!changed) return prev;
      const next = { ...prev, data: { tabs } };
      scheduleAutosave(next);
      return next;
    });
    setSaveStatus("");
  };

  const handleSaveClick = async () => {
    if (schemaFallback) {
      setSaveStatus("error");
      setSaveError(
        "Сохранение недоступно: API provision-report ещё не подключён."
      );
      return;
    }
    if (!isToday) {
      setSaveStatus("error");
      setSaveError("Редактирование доступно только за текущую дату.");
      return;
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    await persist(reportRef.current);
  };

  const handleExport = () => {
    if (!activeTabMeta) return;
    setExporting(true);
    try {
      const title = pageTitle;
      const dateStr = formatReportDateRu(reportDate).replace(/\./g, "-");
      exportProvisionReportExcel({
        title,
        reportDate,
        columns: activeColumns,
        rows: gridRows,
        fileName: `${activeTabMeta.title}_${dateStr}.xlsx`,
      });
    } finally {
      setExporting(false);
    }
  };

  const statusText =
    saveStatus === "saving"
      ? "Сохранение…"
      : saveStatus === "saved"
      ? "Сохранено"
      : saveStatus === "error"
      ? "Ошибка сохранения"
      : "";

  return (
    <DashboardLayout>
      <Stack spacing={2} sx={{ p: 1 }}>
        {!access.canView ? (
          <Alert severity="error">
            Нет доступа к модулю «Обеспеченность». Требуется роль
            SuperAdministrator, Administrator, AdministratorReadonly или
            ProvisionSupervisor.
          </Alert>
        ) : !canAccessThisLab ? (
          <Alert severity="warning">
            Нет доступа к этой лаборатории. Заведующий обеспеченности видит и
            заполняет только отчёт своей лаборатории
            {employeeWithDepartment?.department?.name
              ? ` («${employeeWithDepartment.department.name}»)`
              : ""}
            . Укажите лабораторию в карточке сотрудника.
          </Alert>
        ) : (
          <>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              Обеспеченность · {labTitle}
            </Typography>
            <Typography variant="h5" component="h1">
              {pageTitle}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <FilterDatePicker
              label="Дата отчёта"
              value={reportDate}
              handleFunction={handleDateChange}
              size="small"
            />
            <LoadingButton
              variant="contained"
              startIcon={<SaveIcon />}
              loading={saving}
              onClick={handleSaveClick}
              disabled={loading || isReadonly}
            >
              Сохранить
            </LoadingButton>
            <LoadingButton
              variant="outlined"
              startIcon={<DownloadIcon />}
              loading={exporting}
              onClick={handleExport}
              disabled={loading || !gridRows.length}
            >
              Скачать Excel
            </LoadingButton>
            {statusText && (
              <Typography
                variant="body2"
                color={saveStatus === "error" ? "error" : "text.secondary"}
              >
                {statusText}
              </Typography>
            )}
          </Stack>
        </Stack>

        {schemaFallback && (
          <Alert severity="warning">
            Не удалось загрузить{" "}
            <code>GET /api/v1/provision-report/schema</code> — показан локальный
            fallback. Проверьте ответ бэка (tabs/columnGroups). Сохранение
            отключено, пока schema не ответит 200.
          </Alert>
        )}
        {saveError && <Alert severity="error">{saveError}</Alert>}
        {!isToday && access.canView && canAccessThisLab && (
          <Alert severity="info">
            Просмотр архива: отчёт за прошлую дату нельзя изменять. Редактирование
            доступно только за сегодня.
          </Alert>
        )}

        {schema?.tabs?.length > 0 && (
          <Tabs
            value={activeSectionKey || schema.tabs[0].sectionKey}
            onChange={handleSectionChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {schema.tabs.map((t) => (
              <Tab key={t.sectionKey} label={t.title} value={t.sectionKey} />
            ))}
          </Tabs>
        )}

        {activeTabMeta?.fundingModes?.length > 0 && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={fundingMode}
            onChange={(_e, v) => v && setFundingMode(v)}
          >
            {activeTabMeta.fundingModes.map((m) => (
              <ToggleButton key={m.key} value={m.key}>
                {m.title}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}

        {loading ? (
          <Typography color="text.secondary">Загрузка…</Typography>
        ) : (
          <ProvisionReportGrid
            columns={activeColumns}
            rows={gridRows}
            onCellChange={handleCellChange}
            employee={employeeWithDepartment}
            access={access}
            readOnly={isReadonly}
          />
        )}
          </>
        )}
      </Stack>
    </DashboardLayout>
  );
};

function normalizeDataForSave(report) {
  if (!report?.data?.tabs) return report;
  const tabs = report.data.tabs.map((tab) => ({
    ...tab,
    rows: (tab.rows || []).map((row) => {
      const cells = { ...(row.cells || {}) };
      Object.keys(cells).forEach((key) => {
        const v = cells[key];
        if (typeof v === "string" && /^-?\d+,\d+$/.test(v.trim())) {
          cells[key] = parseDecimalComma(v);
        } else if (typeof v === "string" && /^-?\d+\.\d+$/.test(v.trim())) {
          cells[key] = Number(v);
        }
      });
      return { ...row, cells };
    }),
  }));
  return {
    ...report,
    reportDate:
      typeof report.reportDate === "string"
        ? report.reportDate
        : toApiDate(report.reportDate),
    data: { tabs },
  };
}

export default ProvisionReport;
